import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { LoadingLabel } from "@/components/ui/loading-label";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { SquareTerminal } from "lucide-react";
import { useState } from "react";

/**
 * `/projects/new` — admin-only. Pairs the Hub side (name + customer)
 * with a GitLab repository: one Hub hasn't imported yet, or a new one
 * created in a picked group. A new customer, Clockify project and
 * Seafile folder can be created in the same submit.
 *
 *   * GitLab repo: a combobox driven by
 *     `GET /api/projects/available-gitlab-repos` — server filters
 *     out repos already linked to a Hub project, so the user only
 *     sees real picks. Picking a repo seeds the project name from
 *     the GitLab name (still editable).
 *   * Mirrored from: optional upstream URL (+ label) when the GitLab
 *     repo is a push mirror of a repo hosted elsewhere — a display
 *     link, and what a tracker's external-repo evidence pairs to.
 *   * Customer: dropdown from `/api/customers?pageSize=200` or a new
 *     name, required server-side.
 *
 * Admin-only on the server (`POST /api/projects` → 403 otherwise);
 * the loader pre-fetches its data with the same gate via
 * `/api/projects/available-gitlab-repos` and bounces to /my on
 * 403. On submit the loader's payload is consumed entirely — no
 * partial-state survives a refresh.
 */

type GitLabRepo = {
  id: string;
  name: string;
  pathWithNamespace: string;
  description: string | null;
};

type GitLabGroup = { id: string; path: string; name: string };
type CustomerOption = { id: string; name: string };
type ClockifyProjectOption = { id: string; name: string; archived: boolean };

type LoaderData = {
  repos: GitLabRepo[];
  groups: GitLabGroup[];
  seafileFolders: boolean;
  customers: CustomerOption[];
  clockifyProjects: ClockifyProjectOption[];
};

export const Route = createFileRoute("/_app/projects/new")({
  loader: async (): Promise<LoaderData> => {
    try {
      const [available, customers, clockify] = await Promise.all([
        api.get<{ repos: GitLabRepo[]; groups: GitLabGroup[]; seafileFolders: boolean }>(
          "/api/projects/available-gitlab-repos",
        ),
        api.get<{ customers: CustomerOption[] }>("/api/customers?pageSize=200"),
        // Combobox for the Clockify project. A cold cache returns
        // []; the admin can still create the project and bind a
        // Clockify project later from settings.
        api
          .get<{ projects: ClockifyProjectOption[] }>("/api/projects/available-clockify-projects")
          .catch(() => ({ projects: [] })),
      ]);
      return {
        repos: available.repos,
        groups: available.groups ?? [],
        seafileFolders: available.seafileFolders ?? false,
        customers: customers.customers,
        clockifyProjects: clockify.projects ?? [],
      };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: NewProjectPage,
});

const NEW = "__new__";

const inputClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type CreateBody = {
  name: string;
  gitlabProjectId?: string;
  newRepo?: { groupId?: string; newGroup?: { name: string }; path?: string };
  externalRepoUrl?: string;
  externalRepoLabel?: string;
  customerId?: string;
  newCustomer?: { name: string };
  clockifyProjectId?: string;
  createClockifyProject?: boolean;
  createSeafileFolder?: boolean;
};

/** Mirrors server/lib/slugify, the default repo path. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function NewProjectPage() {
  const { repos, groups, customers, clockifyProjects, seafileFolders } = Route.useLoaderData();
  const navigate = useNavigate();

  const [repoMode, setRepoMode] = useState<"existing" | "new">(
    repos.length === 0 ? "new" : "existing",
  );
  const [gitlabProjectId, setGitlabProjectId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groupTouched, setGroupTouched] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupNameTouched, setNewGroupNameTouched] = useState(false);
  const [repoPath, setRepoPath] = useState("");
  const [externalRepoUrl, setExternalRepoUrl] = useState("");
  const [externalRepoLabel, setExternalRepoLabel] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [clockifyProjectId, setClockifyProjectId] = useState(NEW);
  const [createSeafileFolder, setCreateSeafileFolder] = useState(seafileFolders);
  const [showCommand, setShowCommand] = useState(false);

  function pickRepo(id: string) {
    setGitlabProjectId(id);
    // Seed the project name from the GitLab name on first pick.
    // Once the user has typed into the name field on their own,
    // stop overwriting their input.
    if (!nameTouched) {
      const repo = repos.find((r) => r.id === id);
      if (repo) setName(repo.name);
    }
  }

  const create = useApiMutation(
    (body: CreateBody) => api.post<{ slug: string; warnings?: string[] }>("/api/projects", body),
    {
      onSuccess: (project) => {
        const warnings = project.warnings ?? [];
        toast(
          warnings.length > 0
            ? { title: "Project created", description: warnings.join(" "), variant: "destructive" }
            : { title: "Project created", variant: "success" },
        );
        navigate({ to: "/projects/$slug", params: { slug: project.slug } });
      },
      errorMessage: (err) => (err instanceof Error ? err.message : "Error"),
      onError: (msg) =>
        toast({ title: "Couldn't create project", description: msg, variant: "destructive" }),
    },
  );

  const newCustomer = customerId === NEW;
  const customerName = (
    newCustomer ? newCustomerName : (customers.find((c) => c.id === customerId)?.name ?? "")
  ).trim();
  // Groups go one per customer, so the customer picks the group until the admin does.
  const customerSlug = slugify(customerName);
  const suggestedGroupId = customerSlug
    ? (groups.find((g) => slugify(g.name) === customerSlug || slugify(g.path) === customerSlug)
        ?.id ?? NEW)
    : "";
  const effectiveGroupId = groupTouched ? groupId : suggestedGroupId;
  const creatingGroup = effectiveGroupId === NEW;
  const effectiveNewGroupName = (newGroupNameTouched ? newGroupName : customerName).trim();
  const group = groups.find((g) => g.id === effectiveGroupId);
  const groupPath = creatingGroup ? slugify(effectiveNewGroupName) : group?.path;

  const repoReady = repoMode === "existing" ? Boolean(gitlabProjectId) : Boolean(groupPath);
  const customerReady = newCustomer ? Boolean(newCustomerName.trim()) : Boolean(customerId);
  const ready = repoReady && customerReady && Boolean(name.trim());
  const defaultPath = slugify(name);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (create.pending || !ready) return;
    await create.mutate({
      name: name.trim(),
      ...(repoMode === "existing"
        ? { gitlabProjectId }
        : {
            newRepo: {
              ...(creatingGroup
                ? { newGroup: { name: effectiveNewGroupName } }
                : { groupId: effectiveGroupId }),
              ...(repoPath.trim() ? { path: repoPath.trim() } : {}),
            },
          }),
      ...(newCustomer ? { newCustomer: { name: newCustomerName.trim() } } : { customerId }),
      ...(externalRepoUrl.trim()
        ? {
            externalRepoUrl: externalRepoUrl.trim(),
            ...(externalRepoLabel.trim() ? { externalRepoLabel: externalRepoLabel.trim() } : {}),
          }
        : {}),
      ...(clockifyProjectId === NEW
        ? { createClockifyProject: true }
        : clockifyProjectId
          ? { clockifyProjectId }
          : {}),
      ...(createSeafileFolder ? { createSeafileFolder: true } : {}),
    });
  }

  const repoOptions = repos.map((r) => ({
    value: r.id,
    // Combobox renders a single line; pair display name with the
    // GitLab path so two repos with the same name (`api` in two
    // groups) are distinguishable.
    label: `${r.name} — ${r.pathWithNamespace}`,
  }));

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">New project</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Import a GitLab repository into Hub or start a new one. Issues, members and milestones
          sync over on the next periodic GitLab tick; Vault collections are created right after.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Plan it with the creator</CardTitle>
          <CardDescription>
            In lair on your machine: describe the project, hand over the materials, plan it
            together. The creator then creates the project, its repo, wiki, releases and first
            tickets.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" asChild>
            <a href="lair://creator" onClick={() => setShowCommand(true)}>
              <SquareTerminal className="h-4 w-4 mr-1" />
              Open in terminal
            </a>
          </Button>
          {showCommand && (
            <p className="text-xs text-muted-foreground">
              Nothing opened? Install lair (
              <code className="rounded bg-muted px-1">
                curl -fsSL {window.location.origin}/install.sh | sh
              </code>
              ) or run <code className="rounded bg-muted px-1">lair creator</code>.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>
            Pick the repo and the customer it belongs to. Anything that doesn't exist yet can be
            created here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium leading-none">
                Customer <span className="text-destructive">*</span>
              </span>
              <Select
                aria-label="Customer"
                value={customerId}
                onChange={setCustomerId}
                options={[
                  { value: "", label: "— pick customer —" },
                  { value: NEW, label: "+ New customer" },
                  ...customers.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
              {newCustomer ? (
                <input
                  aria-label="New customer name"
                  type="text"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  placeholder="Customer name"
                  className={inputClass}
                />
              ) : null}
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium leading-none">
                GitLab repository <span className="text-destructive">*</span>
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  aria-pressed={repoMode === "existing"}
                  variant={repoMode === "existing" ? "default" : "outline"}
                  onClick={() => setRepoMode("existing")}
                >
                  Existing repo
                </Button>
                <Button
                  type="button"
                  size="sm"
                  aria-pressed={repoMode === "new"}
                  variant={repoMode === "new" ? "default" : "outline"}
                  onClick={() => setRepoMode("new")}
                >
                  New repo
                </Button>
              </div>
              {repoMode === "existing" ? (
                <>
                  <Combobox
                    aria-label="GitLab repository"
                    placeholder={
                      repos.length === 0 ? "All GitLab repos are imported" : "Search GitLab repos…"
                    }
                    emptyLabel="No matching repos"
                    value={gitlabProjectId}
                    onChange={pickRepo}
                    options={repoOptions}
                  />
                  <p className="text-xs text-muted-foreground">
                    Only repos Hub hasn't imported yet show up here.
                  </p>
                </>
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Combobox
                      aria-label="GitLab group"
                      placeholder="Pick a group…"
                      emptyLabel="No matching groups"
                      value={effectiveGroupId}
                      onChange={(id) => {
                        setGroupId(id);
                        setGroupTouched(true);
                      }}
                      options={[
                        { value: NEW, label: "+ New group" },
                        ...groups.map((g) => ({ value: g.id, label: g.path })),
                      ]}
                    />
                    <input
                      aria-label="Repository path"
                      type="text"
                      value={repoPath}
                      onChange={(e) => setRepoPath(e.target.value)}
                      placeholder={defaultPath || "repo-path"}
                      className={inputClass}
                    />
                  </div>
                  {creatingGroup ? (
                    <input
                      aria-label="New group name"
                      type="text"
                      value={effectiveNewGroupName}
                      onChange={(e) => {
                        setNewGroupName(e.target.value);
                        setNewGroupNameTouched(true);
                      }}
                      placeholder="Group name, usually the customer"
                      className={inputClass}
                    />
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    {creatingGroup ? "A new private group and a" : "A"} private repo with a README
                    on <code>main</code>
                    {groupPath ? (
                      <>
                        {" "}
                        at <code>{`${groupPath}/${repoPath.trim() || defaultPath}`}</code>
                      </>
                    ) : null}
                    . The group follows the customer until you pick one.
                  </p>
                </>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <div className="space-y-2">
                <label htmlFor="externalRepoUrl" className="text-sm font-medium leading-none">
                  Mirrored from
                </label>
                <input
                  id="externalRepoUrl"
                  type="url"
                  value={externalRepoUrl}
                  onChange={(e) => setExternalRepoUrl(e.target.value)}
                  placeholder="https://github.com/org/repo"
                  className={inputClass}
                />
                <p className="text-xs text-muted-foreground">
                  Optional — the upstream this GitLab repo is a push mirror of. Time tracked in a
                  checkout of it is proposed here.
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="externalRepoLabel" className="text-sm font-medium leading-none">
                  Label
                </label>
                <input
                  id="externalRepoLabel"
                  type="text"
                  value={externalRepoLabel}
                  onChange={(e) => setExternalRepoLabel(e.target.value)}
                  placeholder="GitHub"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                Display name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameTouched(true);
                }}
                placeholder="My project"
                className={inputClass}
              />
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium leading-none">Clockify project</span>
              <Select
                aria-label="Clockify project"
                value={clockifyProjectId}
                onChange={setClockifyProjectId}
                options={[
                  { value: NEW, label: "+ Create a new Clockify project" },
                  { value: "", label: "— not bound to Clockify (set later) —" },
                  ...clockifyProjects.map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.archived ? " (archived)" : ""}`,
                  })),
                ]}
              />
              <p className="text-xs text-muted-foreground">
                A new one is private and named after the project. When the project already tracks
                time in Clockify, pick that one instead.
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={createSeafileFolder}
                disabled={!seafileFolders}
                onChange={(e) => setCreateSeafileFolder(e.target.checked)}
              />
              <span>
                Create a Seafile folder
                <span className="block text-xs text-muted-foreground">
                  {seafileFolders
                    ? "In the Projekty library, named after the project. An existing folder of that name is bound instead."
                    : "Seafile isn't managed by Hub here; bind a folder later on /admin/seafile-folders."}
                </span>
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={create.pending || !ready}
                data-testid="new-project-submit"
              >
                {create.pending ? <LoadingLabel text="Creating" /> : "Create project"}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={create.pending}
                onClick={() => void navigate({ to: "/projects" })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
